import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Waves, Home as HomeIcon, Trophy, Calendar, FileText, CreditCard, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import CommunityCalendarWidget from "@/components/CommunityCalendarWidget";
import LaborDayEventCard from "@/components/LaborDayEventCard";

const HERO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_hoa-connect-8/artifacts/y7jq4mhl_ChatGPT%20Image%20Jul%202%2C%202026%2C%2008_58_12%20AM.png";
const POOL = "https://customer-assets-lxgj4vgw.emergentagent.net/job_hoa-connect-8/artifacts/u97vlurr_Pool.png";
const TENNIS = "https://images.pexels.com/photos/12029162/pexels-photo-12029162.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function HomePage() {
  const { user } = useAuth();
  const portalTarget = user ? "/portal" : "/register";
  const paymentsTarget = "/portal/pay";
  return (
    <div data-testid="home-page">
      {/* Hero — image centered with textured side bands */}
      <section
        className="w-full flex justify-center bg-[#EADCC8]"
        style={{
          backgroundImage: `
            radial-gradient(circle at 25% 20%, rgba(200,102,80,0.10) 0, transparent 45%),
            radial-gradient(circle at 78% 78%, rgba(44,76,59,0.12) 0, transparent 55%),
            repeating-linear-gradient(135deg, rgba(44,76,59,0.06) 0 1px, transparent 1px 14px),
            repeating-linear-gradient(45deg, rgba(200,102,80,0.045) 0 1px, transparent 1px 22px)
          `,
          backgroundSize: "auto, auto, 28px 28px, 44px 44px",
        }}
      >
        <img
          src={HERO}
          alt="Saddle Ridge, Savoy Chase Crossing, and The Trails at Savoy Chase — three neighborhood entrances"
          className="w-auto h-auto max-h-[42vh] sm:max-h-[48vh] max-w-full object-contain block drop-shadow-[0_20px_40px_rgba(44,76,59,0.15)]"
        />
      </section>

      {/* Headline & CTAs — sits below the image, always fully readable */}
      <section className="relative bg-[#F9F8F6] border-b border-[#D5CFC4]">
        <div className="max-w-7xl mx-auto px-6 py-14 lg:py-20">
          <p className="text-[#C86650] tracking-[0.3em] text-xs uppercase mb-5 animate-slide-up stagger-1">
            Established Community · Est. 1998
          </p>
          <h1 className="font-serif text-[#1A2421] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl animate-slide-up stagger-2">
            Building a Better Community,<br />Together.
          </h1>
          <p className="text-[#5C6A64] mt-6 text-lg max-w-2xl leading-relaxed animate-slide-up stagger-3">
            Serving Savoy Chase, Saddle Ridge, and The Trails with a commitment to communication, transparency, and preserving the community we share.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 animate-slide-up stagger-4">
            <Link to={portalTarget}>
              <Button
                data-testid="hero-cta-portal"
                size="lg"
                className="rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white px-8 h-12 text-base"
              >
                {user ? `Open your dashboard` : "Access Member Portal"} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/announcements">
              <Button
                data-testid="hero-cta-news"
                variant="outline"
                size="lg"
                className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white px-8 h-12 text-base"
              >
                Community News
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Labor Day event notice */}
      <section className="max-w-7xl mx-auto px-6 pt-12">
        <LaborDayEventCard variant="public" />
      </section>

      {/* Feature strip */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { icon: CreditCard, label: "Pay Dues Online", desc: "Secure Stripe checkout", to: paymentsTarget, testid: "feature-pay-dues" },
            { icon: Calendar, label: "Book Amenities", desc: "Clubhouse, pool, courts", to: "/amenities", testid: "feature-book-amenities" },
            { icon: FileText, label: "Document Library", desc: "Bylaws & financials", to: "/documents", testid: "feature-documents" },
            { icon: Bell, label: "Announcements", desc: "Never miss an update", to: "/announcements", testid: "feature-announcements" },
          ].map((f) => (
            <Link
              key={f.label}
              to={f.to}
              data-testid={f.testid}
              className="block p-6 rounded-2xl border border-[#D5CFC4] bg-white hover:shadow-[0_8px_30px_rgba(44,76,59,0.08)] hover:-translate-y-1 hover:border-[#2C4C3B] transition-all duration-300"
            >
              <f.icon className="w-6 h-6 text-[#C86650] mb-4" />
              <p className="font-serif text-xl text-[#1A2421]">{f.label}</p>
              <p className="text-sm text-[#5C6A64] mt-1">{f.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Amenities Bento */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-12 max-w-2xl">
          <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-3">Amenities</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A2421] leading-tight">
            Three neighborhoods,<br />shared amenities that feel like home.
          </h2>
        </div>
        <div className="grid md:grid-cols-6 md:grid-rows-2 gap-4 h-[520px]">
          <div className="relative rounded-2xl overflow-hidden md:col-span-4 md:row-span-2 group">
            <img src={POOL} alt="Pool & Clubhouse" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-0 left-0 p-8">
              <Waves className="text-white w-7 h-7 mb-3" />
              <p className="font-serif text-white text-3xl">Pool & Clubhouse</p>
              <p className="text-[#EADCC8] mt-1 text-sm">Open daily 6 AM – 10 PM</p>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden md:col-span-2 bg-[#2C4C3B] p-6 flex flex-col justify-between">
            <HomeIcon className="text-[#EADCC8] w-6 h-6" />
            <div>
              <p className="font-serif text-2xl text-white leading-tight">The Clubhouse</p>
              <p className="text-[#EADCC8] text-sm mt-2">Reservable for private events and community gatherings.</p>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden md:col-span-2 group">
            <img src={TENNIS} alt="Tennis Courts" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-0 left-0 p-5">
              <Trophy className="text-white w-6 h-6 mb-2" />
              <p className="font-serif text-white text-2xl">Tennis Courts</p>
            </div>
          </div>
        </div>
        <div className="flex justify-center mt-8">
          <Link to="/amenities">
            <Button
              data-testid="home-book-amenities-btn"
              size="lg"
              className="rounded-full bg-[#2C4C3B] hover:bg-[#233D2F] text-white px-8 h-12"
            >
              Book an Amenity <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Community calendar — below amenities */}
      <CommunityCalendarWidget />

      {/* Community values */}
      <section className="bg-[#EADCC8] py-24 mt-16">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-3">Our Board</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#2C4C3B] leading-tight mb-6">
              Homeowners looking out for homeowners.
            </h2>
            <p className="text-[#1A2421] leading-relaxed text-base mb-4">
              Our HOA is entirely resident-led. The board meets quarterly and welcomes homeowner input on landscaping standards, community events, architectural review, and annual budget planning.
            </p>
            <p className="text-[#5C6A64] text-sm leading-relaxed">
              Annual meetings will be announced soon and will be held at the clubhouse. Meeting minutes and any updates to HOA documents are published in the member document library within 30 days.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact">
                <Button data-testid="home-contact-btn" className="rounded-full bg-[#2C4C3B] hover:bg-[#233D2F] text-white">
                  Contact the Board
                </Button>
              </Link>
              <Link to="/documents">
                <Button data-testid="home-docs-btn" variant="outline" className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white">
                  Read Documents
                </Button>
              </Link>
              <Link to="/get-involved">
                <Button data-testid="home-volunteer-btn" variant="outline" className="rounded-full border-[#C86650] text-[#C86650] hover:bg-[#C86650] hover:text-white">
                  Get Involved
                </Button>
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { n: "3", l: "Neighborhoods" },
              { n: "156", l: "Homes" },
              { n: "1998", l: "Established" },
            ].map((s) => (
              <div key={s.l} className="bg-white rounded-2xl p-8 border border-[#D5CFC4]">
                <p className="font-serif text-5xl text-[#2C4C3B]">{s.n}</p>
                <p className="text-sm text-[#5C6A64] mt-2">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
